import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useDeleteAccount } from '../../api/queries/auth';
import type { User } from '../../api/types';
import { Modal } from '../../components/Modal';
import { PasswordField } from '../../components/PasswordField';
import { useT } from '../../i18n';
import { useAuth } from '../../providers/AuthProvider';
import { useToast } from '../../providers/ToastProvider';

const CONFIRM_WORD = 'DELETE';

/**
 * Deleting an account is irreversible, so it asks for two independent things:
 * the password (proves it's you) and the word DELETE (proves you meant it).
 */
export function DeleteAccountModal({ user, onClose }: { user: User; onClose: () => void }) {
  const { t } = useT();
  const { setUser } = useAuth();
  const remove = useDeleteAccount();
  const toast = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await remove.mutateAsync({ password });
      setUser(null);
      // Purge everything the old session cached, but leave ['auth','me'] in place
      // — removing the query AuthProvider observes orphans it.
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' });
      toast(t('daDeleted'), 'good');
      navigate('/', { replace: true });
    } catch (err) {
      // Blocked (e.g. active bookings) or wrong password — surface the reason.
      toast((err as Error).message, 'bad');
    }
  };

  return (
    <Modal title={t('menuDeleteAccount')} onClose={onClose} maxWidth={440}>
      <form onSubmit={submit}>
        <div className="danger-note">
          <i className="ri-error-warning-line" />
          <div>
            <b>{t('daCannotUndo')}</b>
            <p className="muted">
              {t('daBody')}{' '}
              {user.role === 'manager' ? `${t('daManagerNote')} ` : ''}
              {t('daCancelFirst')} {t('daPastBookings')}
            </p>
          </div>
        </div>

        <label>{t('password')}</label>
        <PasswordField
          autoComplete="current-password"
          ariaLabel={t('password')}
          value={password}
          onChange={setPassword}
        />

        <label>{t('daTypeToConfirm', { word: CONFIRM_WORD })}</label>
        <input value={confirm} onChange={(e) => setConfirm(e.target.value)} />

        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" className="btn ghost" onClick={onClose}>
            {t('daKeepAccount')}
          </button>
          <button
            className="btn bad"
            disabled={remove.isPending || !password || confirm !== CONFIRM_WORD}
          >
            {remove.isPending ? t('daDeleting') : t('menuDeleteAccount')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
